import { useFormContext } from 'react-hook-form';

import { FormControlLabel, styled } from '@mui/material';
import posthog from 'posthog-js';

import { useState } from 'react';

import {
  Autocomplete,
  Checkbox,
  Form,
  Radio,
  Tooltip,
  Typography,
} from '@app-velocity/ui';
import { useI18n } from '@app-velocity/i18n';

import { AIIcon, InfoOutlineIcon, LinkIcon } from '@app-velocity/icons';

import { CreateScenarioDto } from '../../../../../../../dto/CreateScenarioDto';
import { usePromptSuggestions } from './hooks/usePromptSuggestions';
import { SimulationAmountLabel } from './components/SimulationAmountLabel';

const SuggestionIcon = styled('div')({
  flex: '0 1 auto',
});

const SuggestionText = styled('div')({
  flex: 1,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  width: '100%',
});

const LabelContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const Fields = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(6),
  flexDirection: 'column',
  maxHeight: 460,
  overflow: 'auto',
}));

const StyledLink = styled('a')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontSize: 13,
  marginLeft: 'auto',
  textDecoration: 'none',
  '&:link,&:visited': {
    color: 'var(--button-button-primary-color)',
  },
  '&:hover': {
    color: 'var(--button-button-primary-color)',
    textDecoration: 'underline',
  },
  '&:active': {
    color: 'var(--button-button-primary-color)',
    textDecoration: 'underline',
  },
  svg: {
    fill: 'var(--button-button-primary-color)',
  },
}));

const StyledAmountOptions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const AmountSection = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const IndexIndicator = styled('div')(({ theme }) => ({
  borderRadius: '50%',
  fontSize: 11,
  fontWeight: 400,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: theme.spacing(5),
  height: theme.spacing(5),
  backgroundColor: '#5F6776',
}));

const FieldHighlight = styled('div')<{ highlighted: boolean }>(
  ({ theme, highlighted }) => ({
    backgroundColor: highlighted ? '#FAFCFF' : '#FDFDFE',
    borderWidth: 1,
    borderStyle: 'solid',
    padding: theme.spacing(4),
    // margin: theme.spacing(-4),
    borderRadius: theme.spacing(2),
    borderColor: highlighted
      ? '#B8D6FC'
      : 'var(--input-input-background-color)',
    boxShadow: highlighted ? '0 1.5px 1.5px 0 rgba(0, 0, 0, 0.1)' : 'unset',
    transition: '.3s all',
  })
);

const CompactField = styled('div')<{ disabled: boolean }>(
  ({ theme, disabled }) => ({
    padding: `0 ${theme.spacing(4)}`,
    opacity: disabled ? 0.25 : 1,
    transition: '.3s all',
    svg: {
      fill: 'var(--text-text-primary-color)',
    },
  })
);

const Field = styled('div')<{ disabled: boolean }>(({ theme, disabled }) => ({
  padding: theme.spacing(4),
  opacity: disabled ? 0.25 : 1,
  transition: '.3s all',
}));

const StyledTypography = styled(Typography)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const ConfigureScenarioForm = () => {
  const { t } = useI18n();
  const [promptFocused, setPromptFocused] = useState(false);

  const { suggestions } = usePromptSuggestions();

  const {
    control,
    setValue,
    watch,
    register,
    formState: { errors: formErrors },
  } = useFormContext<CreateScenarioDto>();

  const writingGoodPromptUrl = import.meta.env.VITE_WRITING_A_GOOD_PROMPT;

  const [introMessage, amount] = watch(['introMessage', 'amount']);

  const handleAmountChange = (val: string) => {
    setValue('amount', val);
    posthog.capture('selected_amount_of_simulations', {
      amount: val,
    });
  };

  const handleFocus = () => {
    setPromptFocused(true);
  };

  const handleBlur = () => {
    setPromptFocused(false);
  };

  return (
    <Fields>
      <FieldHighlight highlighted={promptFocused}>
        <Form.FieldContainer>
          <Form.Label>
            <LabelContainer>
              <IndexIndicator>1</IndexIndicator>
              {t('prototypes:enter_prompt')}
              {writingGoodPromptUrl ? (
                <StyledLink
                  href={writingGoodPromptUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('prototypes:writing_a_good_prompt')}
                  <LinkIcon />
                </StyledLink>
              ) : null}
            </LabelContainer>
          </Form.Label>

          <Autocomplete<CreateScenarioDto>
            options={suggestions}
            name="introMessage"
            control={control}
            formErrors={formErrors}
            placeholder="prototypes:intro_message_placeholder"
            phCaptureId="challenge_prompt"
            onFocus={handleFocus}
            onBlur={handleBlur}
            renderOption={(option) => (
              <>
                <SuggestionIcon>
                  <AIIcon />
                </SuggestionIcon>
                <SuggestionText>{option}</SuggestionText>
              </>
            )}
          />
        </Form.FieldContainer>
      </FieldHighlight>
      <CompactField disabled={!introMessage}>
        <Form.FieldContainer>
          <Tooltip title={t('prototypes:dont_skip_variants_tooltip')}>
            <StyledTypography>
              <FormControlLabel
                label={t('prototypes:dont_skip_variants')}
                control={<Checkbox {...register('dontSkipVariants')} />}
              />
              <InfoOutlineIcon />
            </StyledTypography>
          </Tooltip>
        </Form.FieldContainer>
      </CompactField>
      <Field disabled={!introMessage}>
        <Form.FieldContainer>
          <Form.Label>
            <LabelContainer>
              <IndexIndicator>2</IndexIndicator>{' '}
              {t('prototypes:choose_how_many_simulations')}{' '}
            </LabelContainer>
          </Form.Label>
          <AmountSection>
            <StyledAmountOptions>
              <Radio
                compact
                name="amount"
                value="1"
                checked={amount === '1'}
                label={
                  <SimulationAmountLabel
                    amount="x1"
                    description={t(
                      'prototypes:simulations_amount_description.x1'
                    )}
                  />
                }
                onChange={handleAmountChange}
              />
              <Radio
                compact
                name="amount"
                value="3"
                checked={amount === '3'}
                label={
                  <SimulationAmountLabel
                    amount="x3"
                    description={t(
                      'prototypes:simulations_amount_description.x3'
                    )}
                  />
                }
                onChange={handleAmountChange}
              />
              <Radio
                compact
                name="amount"
                value="10"
                checked={amount === '10'}
                label={
                  <SimulationAmountLabel
                    amount="x10"
                    description={t(
                      'prototypes:simulations_amount_description.x10'
                    )}
                  />
                }
                onChange={handleAmountChange}
              />
              <Radio
                compact
                name="amount"
                value="50"
                checked={amount === '50'}
                label={
                  <SimulationAmountLabel
                    amount="x50"
                    description={t(
                      'prototypes:simulations_amount_description.x50'
                    )}
                  />
                }
                onChange={handleAmountChange}
              />
              <Radio
                compact
                name="amount"
                value="100"
                checked={amount === '100'}
                label={
                  <SimulationAmountLabel
                    amount="x100"
                    description={t(
                      'prototypes:simulations_amount_description.x100'
                    )}
                  />
                }
                onChange={handleAmountChange}
              />
            </StyledAmountOptions>
          </AmountSection>
        </Form.FieldContainer>
      </Field>
    </Fields>
  );
};
